import React, { useEffect, useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { Button, ButtonGroup } from 'components/ui/button'
import { CheckboxInputElement } from 'components/ui/checkbox'
import { Description } from 'components/ui/list-item-label'
import { StatusGuard } from 'components/ui/status-guard'

import { ReleaseControlsStore } from 'domains/release-controls/store'

import { Timestamp } from "generated/webrpc"
import { ReleaseControl, BundleType, BundleTypeValues } from 'generated/mos/releasecontrols'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'

import { areObjectsEqual, pick } from 'helpers/core'
import { timestampToFormattedString } from 'helpers/timestamps'
import { Status, statusSelector } from 'helpers/status'
import { space } from 'helpers/style'

import { ThemeColor } from 'styled/theme'
import styled from 'styled'

const Overlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: ${({ visible }) => visible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  transition: background 0.2s ease;
  backdrop-filter: blur(2px);
`;

const MenuWrapper = styled.div<{ visible: boolean }>`
  position: fixed;
  z-index: 10;
  display: flex;
  width: 350px;
  background: ${({ theme }) => theme.color.white};
  top: 0;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.color.grayL80};
  box-shadow: 1px 2px 5px rgba(0, 0, 0, 0.1);
  ${({ visible }) => !visible && 'display: none;'}
`;

const Heading = styled.h4`
  margin-top: ${space(2)};
  margin-bottom: ${space(4)};
  font-size: ${({ theme }) => theme.font.size.small};
  font-weight: ${({ theme  }) => theme.font.weight.base};
`
const Section = styled.div`
  padding: ${space(2)} ${space(4)};
  border-bottom: 1px solid ${({ theme }) => theme.color.grayL80};
`

const DescriptionList = styled.dl`
  display: flex;
  flex-wrap: wrap;
  margin: 0;
`

const DescriptionTitle = styled.dt`
  padding-bottom: ${space(1)};
  width: 130px;
  color: ${({ theme }) => theme.color.gray};
  font-size: ${({ theme }) => theme.font.size.small};
  font-weight: ${({ theme }) => theme.font.weight.light};
  line-height: 1.6;
`

const DescriptionValue = styled.dd`
  margin-left: 0;
  padding-bottom: ${space(1)};
  width: calc(100% - 130px)
`

const Text = styled.span`
  color: ${({ theme }) => theme.color.gray};
  font-size: ${({ theme }) => theme.font.size.small};
  font-weight: ${({ theme }) => theme.font.weight.light};
  line-height: 1.6;
`

const Square = styled.span<{ color: ThemeColor }>`
  width: 14px;
  height: 14px;
  background-color: ${({ theme, color }) => theme.color[color]};
  border-radius: 4px;
  display: inline-block;
  border: 2px solid ${({ theme }) => theme.color.grayL90};
  margin-right: ${space(2)};
`;

const ReleaseItemLabel = styled.label`
  display: flex;
  align-items: center;
`

const ReleaseStatus = styled.div`
  display: flex;
  justify-items: center;
  align-items: center;
`

const StatusButton = styled(Button)<{ hasBorder?: boolean }>`
  ${({ hasBorder }) => {
    if (!hasBorder) {
      return `
        background-color: transparent;
        border: 0;
        width: auto;
        &:focus:after {
          border-radius: 0;
        }
      `
    }
  }};
`

const Footer = styled.div`
  padding: ${space(4)};
`

const RELEASE_CONTROLS_ROOT_ID = 'release-controls-root'
const BundleTitle = {
  'BUNDLE_TYPE_PRIVATE': 'Private',
  'BUNDLE_TYPE_PUBLIC': 'Public'
}

const createRoot = (id: string) => {
  const root: Element = document.createElement('div')
  root.setAttribute('id', id)
  document.body.appendChild(root)
  return root
}

const ReleaseControls = (
  {
    toggleSelected,
    checkboxes,
    bundleType,
  }: {
    toggleSelected: (key: BundleType) => void;
    checkboxes: { [key: string]: boolean };
    bundleType: BundleType;
  }
) => {
  if(bundleType === 'BUNDLE_TYPE_UNSPECIFIED') return null
  return (
    <ReleaseItemLabel>
      <CheckboxInputElement
        checked={checkboxes[bundleType]}
        name={bundleType}
        onChange={() => toggleSelected(bundleType)}
      />
      <Description>
        <Text>Include in {BundleTitle[bundleType]} bundle</Text>
      </Description>
    </ReleaseItemLabel>
  )
}

const ReleaseStatusItem = (
  {
    bundleType,
    lastPublished,
    changedSincePublish,
  }: {
    bundleType: BundleType;
    lastPublished: Timestamp | undefined;
    changedSincePublish: boolean;
  }
) => {
  if(bundleType === 'BUNDLE_TYPE_UNSPECIFIED') return null

  const color = changedSincePublish ? 'warning' : lastPublished && !changedSincePublish ? 'success': 'grayL80'
  const title = changedSincePublish ? 'Unpublished changes' : lastPublished && !changedSincePublish ? 'Published': 'Never published'
  return (
    <>
      <DescriptionTitle>{BundleTitle[bundleType]}</DescriptionTitle>
      <DescriptionValue>
        <ReleaseStatus>
          <Square color={color} />
          <Text>{title}</Text>
        </ReleaseStatus>
        <Text>{lastPublished ? timestampToFormattedString(lastPublished.value) : '-'}</Text>
      </DescriptionValue>
    </>
  )
}

type ActionProps = Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<ReleaseControlsStore["releaseControls"], "releaseControlsList">

type DirectProps = {
  readonly hasBorder?: boolean;
  readonly entityRef: {
    typename: string;
    id: string;
  } | undefined;
  readonly updateHandler: (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => void;
}

type Props = ActionProps & ConnectedProps & DirectProps;

export const ReleaseControlUpdateView = ({
  releaseControlsList,
  toastNotification,
  entityRef,
  updateHandler,
  hasBorder,
}: Props) => {

  const [ visible, setVisible ] = useState<boolean>(false)
  const [ checkboxes, setCheckboxes ] = useState<{ [key: string]: boolean }>({})

  const buttonRef = useRef<HTMLButtonElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const hasSubmitted = useRef<boolean>(false)
  const root: Element = document.getElementById(RELEASE_CONTROLS_ROOT_ID) || createRoot(RELEASE_CONTROLS_ROOT_ID)

  const releaseControlsListData = statusSelector.data(releaseControlsList)
  
  const releaseControls = releaseControlsListData && entityRef && releaseControlsListData[entityRef.id]

  // Set initial Checkbox values.
  const initialCheckboxes = releaseControls && releaseControls.bundles
    ? releaseControls.bundles.reduce((acc, curr: ReleaseControl.Bundle.Entity) => ({
        ...acc,
        [curr.bundleType] : curr.include
      }), {})
    : Array.from(BundleTypeValues).filter(item => item !== 'BUNDLE_TYPE_UNSPECIFIED').reduce((acc, curr: BundleType) => ({
        ...acc,
        [curr] : false
      }), {})
  
  useEffect(() => {
    if (releaseControlsList.status === Status.Ready && initialCheckboxes) setCheckboxes(initialCheckboxes)
    if (releaseControlsList.status === Status.Ready && hasSubmitted.current) {
      hasSubmitted.current = false
      toastNotification({
        type: 'success',
        text: 'Release controls updated',
      })
    }
    if (releaseControlsList.status === Status.Failed && hasSubmitted.current) {
      hasSubmitted.current = false
      toastNotification({
        type: 'error',
        text: 'Unable to update release controls',
      })
    }
  }, [releaseControlsList.status]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = useCallback(() => {
    if (visible) {
      setVisible(false)
      setCheckboxes(initialCheckboxes)
    }
  }, [visible, initialCheckboxes])

  const handleClick = useCallback((event: MouseEvent) => {
    const target = event.target as Node
    const wasButtonClick = buttonRef.current && buttonRef.current.contains(target)
    const wasWrapperClick = wrapperRef.current && wrapperRef.current.contains(target)
    if (wasButtonClick && !visible) {
      show()
    } else if (!wasButtonClick && !wasWrapperClick && visible) {
      setVisible(false)
      setCheckboxes(initialCheckboxes) 
    }
  }, [visible, initialCheckboxes])

  const onSubmit = () => {
    const bundles = Object.entries(checkboxes)
      .map(([key, value]) => ({
        type: SetReleaseStatusRequest.BundleStatus.refName,
        bundleType: key as BundleType,
        include: value
      }))
    hasSubmitted.current = true
    updateHandler(bundles)
  }

  useEffect(() => {
    document.addEventListener('scroll', handleScroll)
  }, [handleScroll])

  useEffect(() => {
    document.addEventListener('click', handleClick)
  }, [handleClick])

  const show = () => {
    if (!wrapperRef.current || !buttonRef.current) return false

    setVisible(true)

    // get dimensions and positions
    const rect = buttonRef.current.getBoundingClientRect()
    const buttonX = rect.left
    const buttonY = rect.top
    const buttonW = rect.width
    const buttonH = rect.height
    const wrapperW = wrapperRef.current.offsetWidth

    wrapperRef.current.style.left = `${buttonX + buttonW - wrapperW}px`
    wrapperRef.current.style.top = `${buttonY + buttonH + 20}px`
  }

  const toggleSelected = (key: BundleType) => {
    setCheckboxes({
      ...checkboxes,
      [key]: !checkboxes[key],
    })
  }

  const modified = initialCheckboxes && checkboxes && releaseControlsListData && !areObjectsEqual(initialCheckboxes, checkboxes)
  const view = (
    <>
      <Overlay visible={visible} />
      <MenuWrapper ref={wrapperRef} visible={visible}>
        <StatusGuard status={releaseControlsList}>
          <Section>
            <Heading>Publish status</Heading>
            <DescriptionList>
              {releaseControls && releaseControls.bundles
                ? releaseControls.bundles
                  .map(({ lastPublished, bundleType, changedSincePublish }: ReleaseControl.Bundle.Entity) => 
                    <ReleaseStatusItem key={bundleType} bundleType={bundleType} lastPublished={lastPublished} changedSincePublish={changedSincePublish} />
                  )
                : Array.from(BundleTypeValues)
                  .filter(item => item !== 'BUNDLE_TYPE_UNSPECIFIED')
                  .map((bundleType: BundleType) => 
                    <ReleaseStatusItem key={bundleType} bundleType={bundleType} lastPublished={undefined} changedSincePublish={false} />
                  )
              }
            </DescriptionList>
          </Section>
          <Section>
            {Array.from(BundleTypeValues).filter(item => item !== 'BUNDLE_TYPE_UNSPECIFIED').map((bundleType: BundleType) => 
              <ReleaseControls key={bundleType} bundleType={bundleType} toggleSelected={toggleSelected} checkboxes={checkboxes} />
            )}
          </Section>
          <Footer>
            <ButtonGroup>
              <Button
                appearance="secondary"
                onClick={() => {
                  setVisible(false)
                  setCheckboxes(initialCheckboxes)
                }}
                >
                  Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={onSubmit}
                isDisabled={!modified || releaseControlsList.status === Status.Updating}
                >
                  Save
              </Button>
            </ButtonGroup>
          </Footer>
        </StatusGuard>
      </MenuWrapper>
    </>
  )
  
  const publicPublish = releaseControls && releaseControls.bundles && releaseControls.bundles.find(({ bundleType }) => bundleType === 'BUNDLE_TYPE_PUBLIC')
  const publishStatus = publicPublish && publicPublish.changedSincePublish ? 'warning' : publicPublish && publicPublish.lastPublished && !publicPublish.changedSincePublish ? 'success': 'grayL80'
  const buttonTitle = publicPublish && publicPublish.changedSincePublish ? 'Unpublished changes' : publicPublish && publicPublish.lastPublished && !publicPublish.changedSincePublish ? 'Published': 'Never published'

  return (
    <>
      <StatusButton
        ref={buttonRef}
        appearance="secondary"
        hasBorder={hasBorder}
        onClick={(e) => e.stopPropagation()}
      >
        <Square color={publishStatus} />
        {buttonTitle}
      </StatusButton>
      {root ? createPortal(
        view, root
      ) : null}
    </>
  )
}

export const ReleaseControlUpdate = connect<ConnectedProps, ActionProps, DirectProps>(
  (store) => pick(store.releaseControls, "releaseControlsList"),
  {
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(ReleaseControlUpdateView)
